import { useRef, useState, type PointerEvent } from 'react';

import { cn } from '@/lib/utils';

import { MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from './useColumnWidths';

const KEYBOARD_STEP_PX = 16;

/**
 * Drag handle sitting on a table header's right edge. Pointer capture keeps
 * the drag alive once the cursor leaves the 8px hit area; arrow keys nudge the
 * width for keyboard users; double-click hands a reset back to the caller.
 */
export function ColumnResizeHandle({
  width,
  label,
  onResize,
  onReset,
}: {
  width: number;
  label: string;
  onResize: (width: number) => void;
  onReset: () => void;
}) {
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setIsDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={MIN_COLUMN_WIDTH}
      aria-valuemax={MAX_COLUMN_WIDTH}
      title={label}
      tabIndex={0}
      data-dragging={isDragging || undefined}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        dragRef.current = { startX: event.clientX, startWidth: width };
        setIsDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const drag = dragRef.current;
        if (!drag) return;
        onResize(drag.startWidth + event.clientX - drag.startX);
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={(event) => {
        event.stopPropagation();
        onReset();
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          onResize(width - KEYBOARD_STEP_PX);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          onResize(width + KEYBOARD_STEP_PX);
        }
      }}
      className={cn(
        'absolute inset-y-0 right-0 z-20 w-2 cursor-col-resize touch-none select-none',
        'after:absolute after:inset-y-2 after:right-0 after:w-0.5 after:rounded-full after:bg-transparent',
        'hover:after:bg-primary focus-visible:outline-none focus-visible:after:bg-primary',
        'data-[dragging]:after:bg-primary',
      )}
    />
  );
}
